import {
  type RequiredImageDimensions,
  type RequiredVideoRatio,
  FileError,
  type RequiredImageDimensionsFn,
  type RequiredVideoRatioFn,
  VideoOrientation,
  VideoFileRatio,
} from './ImageUploader.types'

const validateImageDimensions = (
  image: HTMLImageElement,
  requiredDimensions: RequiredImageDimensions | RequiredImageDimensionsFn
): boolean => {
  if (typeof requiredDimensions === 'function') {
    return requiredDimensions(image)
  }

  const {
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    width: requiredWidth,
    height: requiredHeight,
  } = requiredDimensions
  const { width, height } = image

  return (
    (!requiredWidth || width === requiredWidth) &&
    (!requiredHeight || height === requiredHeight) &&
    (!minWidth || width >= minWidth) &&
    (!maxWidth || width <= maxWidth) &&
    (!minHeight || height >= minHeight) &&
    (!maxHeight || height <= maxHeight)
  )
}

const validateVideoRatio = (
  video: HTMLVideoElement,
  requiredRatio: RequiredVideoRatio | RequiredVideoRatioFn = null
) => {
  if (typeof requiredRatio === 'function') {
    return requiredRatio(video)
  }
  const { videoHeight, videoWidth } = video
  const { width: requiredWidth, height: requiredHeight } = requiredRatio

  const actualRatio = videoWidth / videoHeight
  const expectedRatio = requiredWidth / requiredHeight

  return actualRatio === expectedRatio
}

/**
 * Determines video orientation based on aspect ratio
 * @param file File object representing the video
 * @returns 'landscape' for 16:9, 'portrait' for 9:16, or 'unknown' for other ratios
 */
export const getVideoOrientation = (file: File): Promise<VideoOrientation> =>
  new Promise(resolve => {
    const video = document.createElement('video')

    video.onloadedmetadata = () => {
      const { videoHeight, videoWidth } = video

      if (videoWidth / videoHeight === VideoFileRatio.LandscapeRatio) {
        resolve(VideoOrientation.Landscape)
      } else if (videoWidth / videoHeight === VideoFileRatio.PortraitRatio) {
        resolve(VideoOrientation.Portrait)
      } else {
        resolve(VideoOrientation.Unknown)
      }
    }

    video.src = URL.createObjectURL(file)
  })

export const encodeFileName = (file: File) =>
  new File([file], encodeURIComponent(file.name), { type: file.type })

export const validateImage = (
  file: File,
  requiredDimensions: RequiredImageDimensions | RequiredImageDimensionsFn = null
) =>
  new Promise<void>((resolve, reject) => {
    const image = new Image()

    // onload only gets called if image.src is a valid image
    image.onload = () => {
      if (requiredDimensions && !validateImageDimensions(image, requiredDimensions)) {
        reject(FileError.InvalidDimensions)
      }

      resolve()
    }

    image.onerror = () => {
      reject(FileError.InvalidFile)
    }

    image.src = URL.createObjectURL(file)
  })

export const validateVideo = (
  file: File,
  requiredRatio: RequiredVideoRatio | RequiredVideoRatioFn = null
) =>
  new Promise<void>((resolve, reject) => {
    const video = document.createElement('video')

    video.onloadedmetadata = () => {
      if (requiredRatio && !validateVideoRatio(video, requiredRatio)) {
        reject(FileError.InvalidDimensions)
      }

      resolve()
    }

    video.onerror = () => {
      reject(FileError.InvalidFile)
    }

    video.src = URL.createObjectURL(file)
  })
